/**
 * Build step 10 — the Money hub (Revenue vs usage, Usage by client, Risk flags), integrations and the Zoho
 * sync (ADR-001 §8.2, §8.10, §10.5, §11.12; guide §5.6, §6.8; operator decisions 2 and 16).
 *
 * Every shape here carries revenue and is served ONLY to managers (`money.read`: founder, ops_ad). There is
 * deliberately no member variant: a member never reaches these routes (403), and the database column grants
 * of §6.3 refuse the revenue columns to the member role besides.
 *
 * Money is a number in INR lakh (`*_lakh`); `mrr_lakh` is per month. Percentages are 0–100.
 */
import { z } from 'zod';
import { IsoDate, Lakh, PageQuery, Percent, Timestamp, Uuid, list, page } from './common.js';
import { ClientRef } from './clients.js';
import { Severity } from './requests.js';
import { FlagKey } from './settings.js';
import { TaskPriority } from './tasks.js';
import { UserRef } from './users.js';
/** Billing month, `YYYY-MM` (as usage periods). */
export const MoneyPeriod = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM, for example 2026-10');
const Text = (min, max) => z.string().trim().min(min).max(max);
/** A percentage that may be negative (a change, a gap between two shares). */
const SignedPct = z.number().min(-100).max(100);
// ============================================================================ zones (§8.10, guide §6.8)
export const MONEY_ZONES = ['healthy_core', 'revenue_at_risk', 'room_to_grow', 'keep_an_eye_on'];
export const MoneyZone = z.enum(MONEY_ZONES).meta({
    id: 'MoneyZone',
    description: 'high utilisation = weighted utilisation ≥ 60; high revenue = Σ mrr ≥ ₹4 lakh a month. high/high healthy_core; ' +
        'low use/high revenue revenue_at_risk; high use/low revenue room_to_grow; low/low keep_an_eye_on.',
});
/** Prototype `ZONE` names and colours, in legend order. The shape (circle/triangle/diamond/square) is the UI's. */
export const MONEY_ZONE_INFO = {
    healthy_core: { label: 'Healthy core', colour: '#0E9F6E' },
    revenue_at_risk: { label: 'Revenue at risk', colour: '#E11D48' },
    room_to_grow: { label: 'Room to grow', colour: '#5B4BE8' },
    keep_an_eye_on: { label: 'Keep an eye on', colour: '#D97706' },
};
/** The zone lines (ADR §8.10). Fixed in v1; exposed so the chart draws its dashed lines from the same numbers. */
export const MONEY_ZONE_LINES = { util_pct: 60, mrr_lakh: 4 };
/** Effort flag fires when the hours share and revenue share differ by this many points or more (§8.10). */
export const EFFORT_GAP_POINTS = 6;
/** Top-3 revenue share above this shows amber. */
export const TOP3_AMBER_PCT = 50;
/** Pure zoning rule, shared by the nightly job, the API and tests. `null` when there is no usage data. */
export function moneyZone(weightedUtil, mrrLakh) {
    if (weightedUtil == null)
        return null;
    const hiU = weightedUtil >= MONEY_ZONE_LINES.util_pct;
    const hiR = mrrLakh >= MONEY_ZONE_LINES.mrr_lakh;
    if (hiU && hiR)
        return 'healthy_core';
    if (hiR)
        return 'revenue_at_risk';
    if (hiU)
        return 'room_to_grow';
    return 'keep_an_eye_on';
}
/**
 * `more_effort` — "Takes more effort than it pays": hours share − revenue share ≥ 6 points.
 * `light_touch` — "Light touch": revenue share − hours share ≥ 6 points. `null` otherwise.
 */
export const EffortFlag = z.enum(['more_effort', 'light_touch']).meta({ id: 'EffortFlag' });
export const EFFORT_FLAG_LABEL = {
    more_effort: 'Takes more effort than it pays',
    light_touch: 'Light touch',
};
/** Pure effort rule on whole-number shares (the prototype rounds both shares before comparing). */
export function effortFlag(hoursSharePct, revenueSharePct) {
    const diff = Math.round(hoursSharePct) - Math.round(revenueSharePct);
    if (diff >= EFFORT_GAP_POINTS)
        return 'more_effort';
    if (diff <= -EFFORT_GAP_POINTS)
        return 'light_touch';
    return null;
}
// ============================================================================ where the money numbers came from
/**
 * Operator decision 16: before the first successful Zoho sync, per-product MRR typed in Settings is the Money
 * view's source (`manual`); from the first success Zoho is the only source (`zoho`). The view labels which.
 */
export const RevenueSource = z.enum(['manual', 'zoho']).meta({ id: 'RevenueSource' });
/** The "Zoho synced 6:02 am" chip and the staleness banner (a failed sync changes nothing, §10.5). */
export const ZohoFreshness = z
    .object({
    source: RevenueSource,
    /** Last successful Zoho sync; `null` when Zoho has never synced. */
    last_success_at: Timestamp.nullable(),
    /** True when the last success is older than 26 hours, or the most recent run failed. Shows the banner. */
    stale: z.boolean(),
    /** Plain-English banner text when `stale`, e.g. "Zoho last synced 2 days ago. Figures are from then." */
    banner: z.string().nullable(),
})
    .meta({ id: 'ZohoFreshness' });
// ============================================================================ GET /money/overview
export const MoneyOverviewQuery = z.object({
    /** Billing month. Default: the current month (IST). */
    period: MoneyPeriod.optional(),
});
export const MoneyClientRow = z
    .object({
    client: ClientRef,
    /** Σ `client_products.mrr` over the client's active products, ₹ lakh per month. */
    mrr_lakh: Lakh,
    /**
     * What Zoho invoiced the client for `period` (Σ invoice lines for the client in that month, mapped or not).
     * `null` before Zoho is connected or when nothing was invoiced that month.
     */
    plan_value_lakh: Lakh.nullable(),
    /** Revenue-weighted utilisation (weights = mrr). `null` when usage data is missing → not plotted. */
    weighted_util: z.number().min(0).max(400).nullable(),
    /** Weighted % vs the client's own baseline; `null` when unknown. */
    trend: z.number().nullable(),
    /** `null` exactly when `weighted_util` is `null` ("not plotted: usage data is missing"). */
    zone: MoneyZone.nullable(),
    /** `clients.monthly_hours` — ops hours a month spent on this client. */
    monthly_hours: z.number().nonnegative(),
    /** This client's share of total MRR, unrounded. */
    revenue_share_pct: Percent,
    /** This client's share of total ops hours, unrounded (0 when nobody logged hours). */
    hours_share_pct: Percent,
    /** Running total of `revenue_share_pct` in the order rows are returned (largest MRR first). */
    cumulative_share_pct: Percent,
    effort_flag: EffortFlag.nullable(),
    /** Open escalations — the "Escalated" chip in the Revenue at risk list. */
    open_escalations: z.number().int().nonnegative(),
})
    .meta({ id: 'MoneyClientRow' });
export const MoneyKpis = z
    .object({
    /** "Monthly revenue": Σ mrr over all clients. */
    total_mrr_lakh: Lakh,
    /** Σ mrr of clients in `revenue_at_risk` (NOT the Today tile, which is annual revenue of at-risk-band clients). */
    revenue_at_risk_lakh: Lakh,
    /** revenue_at_risk_lakh / total_mrr_lakh × 100; 0 when there is no revenue. */
    revenue_at_risk_pct: Percent,
    /** Share of total MRR held by the three largest clients. */
    top3_share_pct: Percent,
    /** top3_share_pct > 50. */
    top3_amber: z.boolean(),
    /** Σ monthly_hours / Σ mrr; `null` when there is no revenue. */
    ops_hours_per_lakh: z.number().nonnegative().nullable(),
    /** Σ monthly_hours ("N hours this month"). */
    total_hours: z.number().nonnegative(),
})
    .meta({ id: 'MoneyKpis' });
export const MoneyOverview = z
    .object({
    period: MoneyPeriod,
    /** The `client_zones` snapshot date the zones and utilisation come from. */
    as_of: IsoDate,
    freshness: ZohoFreshness,
    /** Every active client, largest MRR first (ties by name). Rows with `zone: null` are listed but not plotted. */
    clients: z.array(MoneyClientRow),
    kpis: MoneyKpis,
    /** The info line under each KPI tile, saying which number it is (decision 16: both figures labelled). */
    labels: z.object({ revenue_at_risk: z.string(), monthly_revenue: z.string() }),
})
    .meta({ id: 'MoneyOverview' });
// ============================================================================ GET /money/usage-vs-billing
export const UsageVsBillingQuery = PageQuery.extend({
    /** One client; omit for every client. */
    client_id: Uuid.optional(),
    /** Billing month. Default: the current month (IST). */
    period: MoneyPeriod.optional(),
    product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).optional(),
});
export const UsageVsBillingLine = z
    .object({
    product_key: z.string(),
    product_name: z.string(),
    /** Unit of the plan and used numbers, e.g. "pageviews". */
    unit: z.string(),
    /** The amount the client bought for the period (`client_products.plan`); `null` when none is set. */
    plan: z.number().nonnegative().nullable(),
    /** Used so far this period; `null` when there is no data (not the same as zero use). */
    used: z.number().nonnegative().nullable(),
    /** % of plan used; over 100 is normal. `null` when either number is missing. */
    util: z.number().min(0).max(400).nullable(),
    /** % vs the client's own baseline. */
    trend: z.number().nullable(),
    /** Subscription value for this product, ₹ lakh a month (`client_products.mrr`). */
    mrr_lakh: Lakh.nullable(),
    /** Zoho invoice lines MAPPED to this product for the period. `null` before Zoho or when none. */
    billed_lakh: Lakh.nullable(),
    /**
     * The gap: value paid for but not used, mrr × (100 − util) / 100, floored at 0. `null` when util or mrr is
     * missing. Over-plan use is shown by `util > 100`, not by a negative gap.
     */
    unused_value_lakh: Lakh.nullable(),
    /** `plan − used`, floored at 0, in the product's unit. */
    unused_units: z.number().nonnegative().nullable(),
})
    .meta({ id: 'UsageVsBillingLine' });
export const UsageVsBillingClient = z
    .object({
    client: ClientRef,
    products: z.array(UsageVsBillingLine),
    totals: z.object({
        mrr_lakh: Lakh,
        billed_lakh: Lakh.nullable(),
        unused_value_lakh: Lakh,
        /** Σ invoice lines for this client and period with no product yet — shown, never guessed. */
        unmapped_billed_lakh: Lakh,
    }),
})
    .meta({ id: 'UsageVsBillingClient' });
/** Paged by client, largest unused value first. */
export const UsageVsBillingPage = page(UsageVsBillingClient)
    .extend({ period: MoneyPeriod, as_of: IsoDate, freshness: ZohoFreshness })
    .meta({ id: 'UsageVsBillingPage' });
// ============================================================================ flags (§8.2) — GET /money/flags
/**
 * `open` → `acknowledged` (a person acted: ack, escalate or task) → `cleared` (the rule stopped firing, or the flag was
 * switched off). A (flag, subject) has ONE live hit while it is open or acknowledged — the unique index is on
 * `(flag_key, subject_id) WHERE state IN ('open','acknowledged')` (step10-contract-decisions.md row 4). While the rule
 * still fires the evaluator refreshes that hit (`as_of`, text, a raised severity), acknowledged or not, and never
 * inserts a second one; so a handled flag stays under "Handled". Only `cleared` frees the slot for a fresh hit.
 */
export const FlagHitState = z.enum(['open', 'acknowledged', 'cleared']).meta({ id: 'FlagHitState' });
/**
 * Default severity of each flag's hits (the prototype's High / Medium / Low). The evaluator may raise a hit's
 * severity (e.g. a top-5 client), never lower it below this.
 */
export const FLAG_SEVERITY = {
    util_below_low: 'high',
    usage_below_baseline_2w: 'med',
    no_usage_data_3d: 'high',
    no_touchpoint_10d: 'high',
    email_unanswered_3d: 'med',
    client_task_overdue: 'med',
    task_slipped_3x: 'med',
    blocked_over_3d: 'med',
    request_breach: 'high',
    checklist_item_missed: 'low',
    revenue_at_risk_top5: 'high',
    held_item_over_4h: 'med',
};
export const FlagSubjectKind = z.enum(['client', 'task', 'request', 'held_item', 'person']).meta({ id: 'FlagSubjectKind' });
export const FlagHit = z
    .object({
    id: Uuid,
    flag_key: FlagKey,
    /** The rule chip — `FLAG_INFO[flag_key].label`. */
    rule: z.string(),
    severity: Severity,
    /** Plain-English line, with the numbers, e.g. "₹7.3 L / mo billed, utilisation 40% and falling". */
    text: z.string(),
    /** What the flag is about, so the UI can link to it. `label` is its display name. */
    subject: z.object({ kind: FlagSubjectKind, id: Uuid, label: z.string() }),
    /** The client the subject belongs to; `null` for a flag about a person or internal work. */
    client: ClientRef.nullable(),
    /** The numbers behind `text` (e.g. `{ util: 40, mrr_lakh: 7.3, days: 14 }`). Keys are per flag. */
    numbers: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
    /** Business date the rule last confirmed the hit — refreshed in place while it is open or acknowledged. */
    as_of: IsoDate,
    /** "Since 5 Oct" — the day the hit opened (IST). */
    since: IsoDate,
    opened_at: Timestamp,
    state: FlagHitState,
    /** Who acted on it and how. `null` while open, and for a hit cleared without anyone acting. */
    handled: z
        .object({
        by: UserRef,
        /** The real person, when `by` acted while acting as someone (ADR-006). */
        acting_for: UserRef.nullable(),
        at: Timestamp,
        how: z.enum(['acknowledged', 'escalated', 'task_made']),
        escalation_id: Uuid.nullable(),
        task_id: Uuid.nullable(),
        note: z.string().nullable(),
    })
        .nullable(),
    cleared_at: Timestamp.nullable(),
})
    .meta({ id: 'FlagHit' });
export const ListFlagHitsQuery = PageQuery.extend({
    flag_key: FlagKey.optional(),
    /** Default `open`. `acknowledged` lists the "Handled" rows. */
    state: FlagHitState.default('open'),
    severity: Severity.optional(),
    client_id: Uuid.optional(),
});
/** Worst first: severity, then oldest `since`. `summary` counts ignore `severity`/`state` filters (the stat tiles). */
export const FlagHitPage = page(FlagHit)
    .extend({
    summary: z.object({
        open: z.number().int().nonnegative(),
        high: z.number().int().nonnegative(),
        med: z.number().int().nonnegative(),
        low: z.number().int().nonnegative(),
        acknowledged: z.number().int().nonnegative(),
    }),
})
    .meta({ id: 'FlagHitPage' });
export const FlagHitIdParams = z.object({ id: Uuid });
/** Escalate: severity defaults to the hit's; title to "<rule>: <text>" with no revenue figure; owner is the client's current SPOC. */
export const EscalateFlagBody = z
    .object({
    severity: Severity.optional(),
    title: Text(3, 200).optional(),
    /** Only someone with task.reassign may pick an owner other than the client's SPOC. */
    owner_id: Uuid.optional(),
    note: Text(1, 2000).optional(),
})
    .strict();
/**
 * Make task: title defaults to "<rule>: <text>" with no revenue figure; assignee to the client's SPOC (or the subject's owner for a
 * flag with no client); due to the next working day; priority `high` for a high hit, else `medium`.
 */
export const FlagTaskBody = z
    .object({
    title: Text(3, 200).optional(),
    assignee_id: Uuid.optional(),
    due: IsoDate.optional(),
    priority: TaskPriority.optional(),
})
    .strict();
export const AckFlagBody = z.object({ note: Text(1, 500).optional() }).strict();
// ============================================================================ integrations
/** Speech to text is not here: it runs in the browser only (t-cd196043), so there is no server integration to watch. */
export const INTEGRATION_KEYS = ['zoho', 'gmail', 'calendar', 'llm'];
export const IntegrationKey = z.enum(INTEGRATION_KEYS).meta({ id: 'IntegrationKey' });
/** Settings → Integrations panel titles (prototype wording). */
export const INTEGRATION_LABEL = {
    zoho: 'Zoho billing',
    gmail: 'Email tracking',
    calendar: 'Calendar',
    llm: 'AI summaries',
};
/** `connected` ok; `stale` last success too old; `error` the last run failed; `off` disabled or never set up. */
export const IntegrationStatus = z.enum(['connected', 'stale', 'error', 'off']).meta({ id: 'IntegrationStatus' });
export const IntegrationState = z
    .object({
    integration: IntegrationKey,
    label: z.string(),
    enabled: z.boolean(),
    status: IntegrationStatus,
    last_success_at: Timestamp.nullable(),
    last_error_at: Timestamp.nullable(),
    /** Plain English; never a secret, token or raw upstream body. */
    last_error: z.string().nullable(),
    /** Human summary of the paging cursor, e.g. "Invoices up to 15 Oct". Never the raw cursor. */
    cursor_summary: z.string().nullable(),
    /** Zoho only: invoice lines still needing a product; `null` for the others. */
    unmapped_lines: z.number().int().nonnegative().nullable(),
})
    .meta({ id: 'IntegrationState' });
export const IntegrationList = list(IntegrationState).meta({ id: 'IntegrationList' });
export const SyncStarted = z
    .object({ job_id: Uuid.meta({ description: 'The job_runs id; follow it with GET /jobs/runs?job=zoho.sync' }) })
    .meta({ id: 'SyncStarted' });
// ============================================================================ Zoho invoice lines
export const ZohoInvoiceLine = z
    .object({
    id: Uuid,
    /** Zoho's line item id. */
    zoho_id: z.string(),
    invoice_number: z.string(),
    invoice_date: IsoDate,
    description: z.string(),
    /** Matched on the external id stored on the client, never on name after the first match (§10.5). */
    client_match: z.object({
        client: ClientRef.nullable(),
        zoho_customer_id: z.string(),
        zoho_customer_name: z.string(),
    }),
    amount_lakh: Lakh,
    period: MoneyPeriod,
    /** `null` until a manager picks the product — unmapped lines are shown, never guessed. */
    mapped_product_key: z.string().nullable(),
    mapped_by: UserRef.nullable(),
    mapped_at: Timestamp.nullable(),
})
    .meta({ id: 'ZohoInvoiceLine' });
export const ListInvoiceLinesQuery = PageQuery.extend({
    /** `false` → only lines needing a product (the Settings panel). Omit for all. */
    mapped: z.enum(['true', 'false']).transform((s) => s === 'true').optional(),
    client_id: Uuid.optional(),
    period: MoneyPeriod.optional(),
});
/** Newest invoice first. */
export const ZohoInvoiceLinePage = page(ZohoInvoiceLine).meta({ id: 'ZohoInvoiceLinePage' });
export const InvoiceLineIdParams = z.object({ id: Uuid });
/**
 * Map (or re-map) a line to a product the client buys. A line with no matched client cannot be mapped (409).
 * Re-mapping is allowed and audited; there is no "unmap" — map it to the right product instead.
 */
export const MapInvoiceLineBody = z
    .object({ product_key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'Pick a product') })
    .strict();
// ============================================================================ GET /jobs/runs
export const JobRunStatus = z.enum(['running', 'ok', 'failed']).meta({ id: 'JobRunStatus' });
/** Job names are `<area>.<verb>` from ADR §9, e.g. `zoho.sync`, `flags.evaluate`, `usage.rollup`. */
export const JobName = z.string().regex(/^[a-z][a-z_]*\.[a-z][a-z_]*$/, 'A job name such as zoho.sync');
export const JobRun = z
    .object({
    id: Uuid,
    job: JobName,
    status: JobRunStatus,
    started_at: Timestamp,
    finished_at: Timestamp.nullable(),
    duration_ms: z.number().int().nonnegative().nullable(),
    /**
     * What the run recorded, as the worker wrote it (`job_runs.counts`): mostly counters, e.g.
     * `{ clients: 12, plans: 30, invoice_lines: 41, unmapped: 3 }`, plus a few labels — `usage.parse` writes
     * `{ upload_id: '<uuid>', skipped: true }` or `{ upload_id, rows_total, …, will_retry: false }`, the nightly runs
     * `{ as_of: '2026-10-16', … }`, `checklists.generate` `{ skipped: 'not a working day' }`. Flat; `{}` while running.
     */
    counts: z.record(z.string(), z.union([z.number(), z.string(), z.boolean(), z.null()])),
    /** Plain English; never a secret or raw upstream body. */
    error: z.string().nullable(),
    /** Who started it by hand (`POST /integrations/zoho/sync`); `null` for a scheduled run. */
    triggered_by: UserRef.nullable(),
})
    .meta({ id: 'JobRun' });
export const ListJobRunsQuery = PageQuery.extend({
    job: JobName.optional(),
    status: JobRunStatus.optional(),
    /** Runs started on or after this date (IST). Default: the last 7 days. */
    from: IsoDate.optional(),
});
/** Newest first. */
export const JobRunPage = page(JobRun).meta({ id: 'JobRunPage' });
//# sourceMappingURL=money.js.map